import { useState } from "react";
import { sendPasswordReset } from "./firebase";
import { useTheme } from "./theme";
import { useT } from "./i18n";
import {
  trialDaysLeft, VOICE_CONSENT_VERSION, hasValidVoiceConsent,
  hasVoiceEntitlement, getVoiceLimitSeconds, resolveVoiceBalance, getVoiceTier,
  isOnVoiceTrial, VOICE_TIER_ELITE, ELITE_PRICE_MONTHLY_USD, ELITE_PRICE_YEARLY_USD,
} from "./constants";
import { formatNumber, formatVoiceMinutes, formatVoiceDuration } from "./utils";
import { getManagementLink } from "./api";

// BCP-47 tags for locale-aware date formatting, keyed by the app's base-language names.
const DATE_LOCALES = {
  English: "en-US", Spanish: "es-ES", German: "de-DE", French: "fr-FR", Turkish: "tr-TR",
  Italian: "it-IT", Portuguese: "pt-PT", Russian: "ru-RU", Farsi: "fa-IR", Arabic: "ar-SA",
  Chinese: "zh-CN", Hindi: "hi-IN",
};

// amountCents is Paddle's lowest-denomination integer (e.g. 599 = $5.99).
function formatCurrency(amountCents, currency, locale) {
  if (!Number.isFinite(amountCents) || !currency) return null;
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amountCents / 100);
  } catch {
    return `${(amountCents / 100).toFixed(2)} ${currency}`;
  }
}

export function SettingsView({ user, profile, progress, cards, subscription, isAdminAccount, isSimpleViewer, onBack, onReset, onDeleteAccount, onUpgrade, onPause, onResume, onChangeVoiceTier, onUpdateProfile }) {
  const { darkMode, toggleDark, fontScale, setFontScale } = useTheme();
  const { t, lang } = useT();
  const dateLocale = DATE_LOCALES[lang] || "en-US";
  const [resetStep,       setResetStep]       = useState(false);
  const [deleteStep,      setDeleteStep]      = useState(false);
  const [deleteText,      setDeleteText]      = useState("");
  const [deletePassword,  setDeletePassword]  = useState("");
  const [deleteError,     setDeleteError]     = useState("");
  const [busy,            setBusy]            = useState(false);
  const [exportDone,      setExportDone]      = useState(false);
  const [pwResetSent,     setPwResetSent]     = useState(false);
  const [resetDone,       setResetDone]       = useState(false);
  const [manageOfferOpen, setManageOfferOpen] = useState(false);
  const [pauseOfferOpen,  setPauseOfferOpen]  = useState(false);
  const [pauseBusy,       setPauseBusy]       = useState(false);
  const [pauseError,      setPauseError]      = useState("");
  const [resumeStep,      setResumeStep]      = useState(false);
  const [resumeBusy,      setResumeBusy]      = useState(false);
  const [resumeError,     setResumeError]     = useState("");
  const [manageLinkBusy,  setManageLinkBusy]  = useState(false);
  const [manageLinkError, setManageLinkError] = useState("");
  const [editingName,     setEditingName]     = useState(false);
  const [nameInput,       setNameInput]       = useState("");
  const [nameSaving,      setNameSaving]      = useState(false);
  // A6: Basic<->Elite tier change confirm/busy/error state, same shape as pause/resume above.
  const [tierChangeOfferOpen, setTierChangeOfferOpen] = useState(null); // null | "elite" | "basic"
  const [tierChangeBusy,      setTierChangeBusy]      = useState(false);
  const [tierChangeError,     setTierChangeError]     = useState("");

  const isEmailUser = user?.providerData?.some(p => p.providerId === "password");

  const lastTxn = subscription?.lastTransaction;
  const isPaymentFailed = subscription?.status === "past_due" || lastTxn?.status === "failed";
  const lastChargeAmount = lastTxn ? formatCurrency(lastTxn.amountCents, lastTxn.currency, dateLocale) : null;
  const lastChargeLine = lastChargeAmount ? (
    <div className="settings-row-sub" style={{marginTop:4}}>
      {t("settings.subscriptionLastCharge", {
        amount: lastChargeAmount,
        date: lastTxn.date ? new Date(lastTxn.date).toLocaleDateString(dateLocale) : "—",
        status: lastTxn.status === "failed" ? t("settings.subscriptionLastChargeFailed") : t("settings.subscriptionLastChargePaid"),
      })}
    </div>
  ) : null;

  const initials = (profile?.name || user?.displayName || "?")
    .split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  // A6: item 8 — persistent voice-minutes display, checkable anytime (not just the reactive
  // low-balance banner in the widget, which stays as its own separate thing). Shown whenever
  // voice is entitled at all, independent of which subscription-status branch below applies.
  const voiceEntitled = hasVoiceEntitlement(profile, subscription, isAdminAccount, isSimpleViewer);
  const voiceTier = getVoiceTier(subscription);
  // Pre-subscription trial: no real Paddle tier yet, so labelling the allotment "(Pro)" is
  // misleading — it's the smaller, tier-agnostic trial allotment (VOICE_TRIAL_LIMIT_SECONDS).
  const onVoiceTrial = isOnVoiceTrial(profile, subscription, isAdminAccount, isSimpleViewer);
  const voiceLimitSeconds = getVoiceLimitSeconds(profile, subscription, isAdminAccount, isSimpleViewer);
  const voiceBalanceResolved = resolveVoiceBalance(profile, subscription);
  // "used" is against this cycle's monthly allowance only — capped at the tier limit (a
  // downgrade can leave the raw stored value above it). The purchased pool is shown on its
  // own line below, since it's separate and persistent, not part of the monthly allowance.
  const voiceUsedSeconds = Math.min(voiceBalanceResolved.usedSeconds, voiceLimitSeconds);
  // formatVoiceMinutes (whole minutes) for grants; formatVoiceDuration (to the second) for the
  // metered "used" figure — see utils.js. Shared with the Listen & Speak Lab's balance card.
  const isElite = subscription?.status === "active" && voiceTier === VOICE_TIER_ELITE;
  const isBasicActive = subscription?.status === "active" && voiceTier !== VOICE_TIER_ELITE;

  const handleExport = () => {
    const payload = {
      exported: new Date().toISOString(),
      profile,
      progress: { ...progress, dailyLog: progress?.dailyLog || [] },
      flashcards: cards.map(({ id, turkish, word, stability, difficulty, interval, due, state, lapses }) =>
        ({ id, word: turkish || word, stability, difficulty, interval, due, state, lapses })
      ),
    };
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }));
    a.download = `speafy-data-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    setExportDone(true);
    setTimeout(() => setExportDone(false), 3000);
  };

  const confirmReset = async () => {
    setBusy(true);
    await onReset();
    // Unlike delete/sign-out, a progress reset keeps the user on this screen —
    // close the confirm box ourselves and show a brief success message.
    setBusy(false);
    setResetStep(false);
    setResetDone(true);
    setTimeout(() => setResetDone(false), 4000);
  };

  // Fetches a fresh Paddle customer-portal link and opens it — replaces every
  // former `<a href={subscription.managementUrl}>` read, since that field is
  // always null (Paddle Billing doesn't send it on the webhook events we
  // handle; see getManagementLink's doc comment in api.js). preferredKey
  // picks a more targeted per-subscription link (e.g. "cancel_subscription")
  // when the caller wants one and it's present; otherwise falls back to the
  // general portal overview link.
  const openManageLink = async (preferredKey) => {
    setManageLinkError(""); setManageLinkBusy(true);
    try {
      const { generalUrl, subscriptionLinks } = await getManagementLink();
      const url = (preferredKey && subscriptionLinks?.[preferredKey]) || generalUrl;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else setManageLinkError(t("settings.manageLinkFailedGeneric"));
    } catch {
      setManageLinkError(t("settings.manageLinkFailedGeneric"));
    } finally {
      setManageLinkBusy(false);
    }
  };

  const confirmPause = async () => {
    setPauseError(""); setPauseBusy(true);
    try {
      await onPause();
      setPauseOfferOpen(false);
    } catch {
      // Server error text (proxy/index.js) is plain English for logging purposes only —
      // show a localized generic message so the UI stays fully translated.
      setPauseError(t("settings.pauseFailedGeneric"));
    } finally {
      setPauseBusy(false);
    }
  };

  const confirmResume = async () => {
    setResumeError(""); setResumeBusy(true);
    try {
      await onResume();
      setResumeStep(false);
    } catch {
      setResumeError(t("settings.resumeFailedGeneric"));
    } finally {
      setResumeBusy(false);
    }
  };

  // A6: fires the server-side tier-change request (proxy/index.js's /api/subscription/change-tier
  // — never a client Paddle checkout, see that route's own comment for why). Just reports
  // request success/failure; the actual subscription.tier/pendingTier update lands via the
  // webhook (upgrade) or the route's own synchronous write (downgrade) shortly after.
  const confirmTierChange = async () => {
    setTierChangeError(""); setTierChangeBusy(true);
    try {
      await onChangeVoiceTier(tierChangeOfferOpen);
      setTierChangeOfferOpen(null);
    } catch {
      setTierChangeError(t("settings.tierChangeFailedGeneric"));
    } finally {
      setTierChangeBusy(false);
    }
  };

  const startEditName = () => {
    setNameInput(profile?.name || user?.displayName || "");
    setEditingName(true);
  };

  const cancelEditName = () => setEditingName(false);

  const saveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    setNameSaving(true);
    try {
      await onUpdateProfile({ name: trimmed });
      setEditingName(false);
    } finally {
      setNameSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (deleteText !== "DELETE") return;
    if (isEmailUser && !deletePassword) { setDeleteError("Please enter your password to confirm."); return; }
    setDeleteError(""); setBusy(true);
    try { await onDeleteAccount(isEmailUser ? deletePassword : undefined); }
    catch (e) {
      setDeleteError(
        e.code === "auth/wrong-password" || e.code === "auth/invalid-credential"
          ? "Incorrect password." : "Deletion failed. Please try again."
      );
      setBusy(false);
    }
  };

  return (
    <div className="goals-view">
      <div className="lesson-header">
        <button className="back-btn" onClick={onBack}>{t("lesson.backBtn")}</button>
        <div className="lesson-title">{t("settings.title")}</div>
      </div>

      {/* ── Account ── */}
      <div className="goals-card">
        <div className="goals-section-title">{t("settings.accountSection")}</div>
        <div style={{display:"flex", alignItems:"center", gap:16}}>
          {user?.photoURL
            ? <img src={user.photoURL} alt="" className="settings-account-avatar" />
            : <div className="settings-avatar-initials">{initials}</div>
          }
          <div className="settings-account-info">
            {editingName ? (
              <div style={{display:"flex", flexDirection:"column", gap:8, marginBottom:6}}>
                <input
                  className="text-input"
                  style={{marginBottom:0}}
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  placeholder={t("goals.namePlaceholder")}
                  maxLength={60}
                  autoFocus
                  onKeyDown={e => { if (e.key === "Enter") saveName(); if (e.key === "Escape") cancelEditName(); }}
                />
                <div style={{display:"flex", gap:8}}>
                  <button className="settings-action-btn" disabled={nameSaving || !nameInput.trim()} onClick={saveName}>
                    {nameSaving ? t("settings.nameSaving") : t("settings.nameSaveBtn")}
                  </button>
                  <button className="settings-cancel-btn" onClick={cancelEditName} disabled={nameSaving}>
                    {t("settings.nameCancelBtn")}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{display:"flex", alignItems:"center", gap:10, flexWrap:"wrap"}}>
                <div className="settings-account-name">{profile?.name || user?.displayName || t("header.learner")}</div>
                <button className="settings-action-btn" style={{padding:"4px 10px", fontSize:"0.75rem"}} onClick={startEditName}>
                  {t("settings.changeNameBtn")}
                </button>
              </div>
            )}
            <div className="settings-account-email">{user?.email}</div>
            {isEmailUser ? (
              <div className="settings-account-badge" style={{background:"#EEF2FF",borderColor:"#C7D2FE",color:"#3730A3"}}>
                ✉ {t("settings.emailAccountBadge")}
              </div>
            ) : (
              <div className="settings-account-badge">
                <span>G</span> {t("settings.linkedGoogle")}
              </div>
            )}
          </div>
        </div>
        {isEmailUser && (
          <div className="settings-row" style={{marginTop:16,paddingTop:16,borderTop:"1px solid #F0DFC0"}}>
            <div>
              <div className="settings-row-label">{t("settings.passwordLabel")}</div>
              <div className="settings-row-sub">{t("settings.passwordSub")}</div>
            </div>
            <button
              className="settings-action-btn"
              onClick={async () => { await sendPasswordReset(user.email); setPwResetSent(true); setTimeout(() => setPwResetSent(false), 4000); }}>
              {pwResetSent ? t("settings.passwordSent") : t("settings.passwordResetBtn")}
            </button>
          </div>
        )}
      </div>

      {/* ── Subscription ── */}
      <div className="goals-card">
        <div className="goals-section-title">{t("settings.subscriptionSection")}</div>
        {isSimpleViewer ? (
          <div className="settings-row">
            <div>
              <div className="settings-row-label">{t("settings.subscriptionReviewerLabel")}</div>
              <div className="settings-row-sub">{t("settings.subscriptionReviewerSub")}</div>
            </div>
          </div>
        ) : isAdminAccount ? (
          <div className="settings-row">
            <div>
              <div className="settings-row-label">{t("settings.subscriptionAdminLabel")}</div>
              <div className="settings-row-sub">{t("settings.subscriptionAdminSub")}</div>
            </div>
          </div>
        ) : isPaymentFailed ? (
          <div className="settings-row">
            <div>
              <div className="settings-row-label" style={{color:"#C0392B"}}>
                ⚠ {t("settings.subscriptionPaymentFailedLabel")}
              </div>
              <div className="settings-row-sub">
                {lastTxn?.status === "failed" && lastTxn.date
                  ? t("settings.subscriptionPaymentFailedSub", { date: new Date(lastTxn.date).toLocaleDateString(dateLocale) })
                  : t("settings.subscriptionPaymentFailedSubNoDate")}
              </div>
              {lastChargeLine}
            </div>
            <button
              className="settings-action-btn"
              style={{background:"#C0392B",color:"#fff",borderColor:"#C0392B"}}
              onClick={() => openManageLink("update_subscription_payment_method")}
              disabled={manageLinkBusy}
            >
              {manageLinkBusy ? t("settings.manageLinkLoading") : t("settings.subscriptionUpdatePaymentLink")}
            </button>
          </div>
        ) : subscription?.status === "active" ? (
          <>
            <div className="settings-row">
              <div>
                <div className="settings-row-label">
                  {t(subscription.tier === VOICE_TIER_ELITE ? "settings.subscriptionElitePlan" : "settings.subscriptionProPlan", { plan: subscription.planType === "yearly" ? t("settings.subscriptionPlanAnnual") : t("settings.subscriptionPlanMonthly") })}
                </div>
                <div className="settings-row-sub">
                  {subscription.currentPeriodEnd
                    ? t("settings.subscriptionRenews", { date: new Date(subscription.currentPeriodEnd).toLocaleDateString(dateLocale) })
                    : t("settings.subscriptionActive")}
                </div>
                {lastChargeLine}
              </div>
              <button className="settings-action-btn" onClick={() => openManageLink()} disabled={manageLinkBusy}>
                {manageLinkBusy ? t("settings.manageLinkLoading") : t("settings.subscriptionManageLink")}
              </button>
            </div>
            {manageLinkError && <p style={{color:"#c0392b",fontSize:"0.8125rem",margin:"4px 0 0"}}>{manageLinkError}</p>}

            {/* A6: pending downgrade, mirrors the pausePendingAt banner's shape below —
                written directly by /api/subscription/change-tier's own response (not the
                webhook), since Paddle's documented webhook payloads don't confirm a
                scheduled_change shape for a plain deferred item/price change. */}
            {subscription.pendingTier && (
              <div className="settings-row" style={{borderTop:"1px solid #F0DFC0",marginTop:12,paddingTop:12}}>
                <div>
                  <div className="settings-row-label">{t("settings.tierChangePendingLabel")}</div>
                  <div className="settings-row-sub">
                    {t(subscription.pendingTier === VOICE_TIER_ELITE ? "settings.tierChangePendingToElite" : "settings.tierChangePendingToBasic", {
                      date: subscription.pendingTierEffectiveAt ? new Date(subscription.pendingTierEffectiveAt).toLocaleDateString(dateLocale) : "—",
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* A6: Elite upsell (Basic) / downgrade (Elite) — a server-side tier change on the
                existing subscription, never a second checkout. Hidden entirely once a change is
                already pending, to avoid stacking a second request on top of an unresolved one.
                The upsell is a branded button (a real upgrade action, matching the trial-user
                Upgrade button above); the downgrade stays a quiet text link. */}
            {!subscription.pendingTier && isBasicActive && (
              <div style={{textAlign:"right", marginTop:12}}>
                <button
                  className="settings-action-btn"
                  style={{background:"#7B1E1E",color:"#fff",borderColor:"#7B1E1E"}}
                  onClick={() => { setTierChangeOfferOpen("elite"); setTierChangeError(""); }}
                >
                  {t("settings.tierUpgradeToEliteLink")}
                </button>
              </div>
            )}
            {!subscription.pendingTier && isElite && (
              <div style={{textAlign:"right", marginTop:8}}>
                <button
                  className="report-link"
                  onClick={() => { setTierChangeOfferOpen("basic"); setTierChangeError(""); }}
                >
                  {t("settings.tierDowngradeToBasicLink")}
                </button>
              </div>
            )}

            {tierChangeOfferOpen && (
              <div className="settings-confirm-box">
                <p><strong>{tierChangeOfferOpen === "elite" ? t("settings.tierUpgradeConfirmTitle") : t("settings.tierDowngradeConfirmTitle")}</strong></p>
                <p>
                  {tierChangeOfferOpen === "elite"
                    ? t("settings.tierUpgradeConfirmSub", {
                        price: formatNumber(subscription.planType === "yearly" ? ELITE_PRICE_YEARLY_USD : ELITE_PRICE_MONTHLY_USD, lang),
                        interval: subscription.planType === "yearly" ? t("settings.subscriptionPlanAnnual") : t("settings.subscriptionPlanMonthly"),
                      })
                    : t("settings.tierDowngradeConfirmSub", {
                        date: subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString(dateLocale) : "—",
                      })}
                </p>
                {tierChangeError && <p style={{color:"#c0392b",fontSize:"0.8125rem",margin:"8px 0 0"}}>{tierChangeError}</p>}
                <button className="settings-delete-final-btn" style={tierChangeOfferOpen === "elite" ? {background:"#2E7D32"} : undefined} onClick={confirmTierChange} disabled={tierChangeBusy}>
                  {tierChangeBusy ? t("settings.tierChangeBusy") : (tierChangeOfferOpen === "elite" ? t("settings.tierUpgradeConfirmBtn") : t("settings.tierDowngradeConfirmBtn"))}
                </button>
                <button className="settings-cancel-btn" onClick={() => setTierChangeOfferOpen(null)}>{t("settings.cancelBtn")}</button>
              </div>
            )}

            {
              // Cancel/pause is its own explicit affordance (task: the old code
              // routed the billing/invoices button itself into this offer for
              // monthly subscribers, so clicking what looked like "see my
              // invoices" instead sprang a cancel-flow prompt). Yearly/
              // pending-pause/other states have no pause option to offer, so
              // there's nothing to surface here — cancelling those is reachable
              // from the Paddle portal link above.
              subscription.planType === "monthly" && !subscription.pausePendingAt && (
                <div style={{textAlign:"right", marginTop:8}}>
                  <button className="report-link" onClick={() => { setManageOfferOpen(true); setManageLinkError(""); }}>
                    {t("settings.subscriptionCancelLink")}
                  </button>
                </div>
              )
            }

            {subscription.planType === "monthly" && subscription.pausePendingAt && (
              <div className="settings-row" style={{borderTop:"1px solid #F0DFC0",marginTop:12,paddingTop:12}}>
                <div>
                  <div className="settings-row-label">{t("settings.pausePendingLabel")}</div>
                  <div className="settings-row-sub">
                    {t("settings.pausePendingSub", { date: new Date(subscription.pausePendingAt).toLocaleDateString(dateLocale) })}
                  </div>
                </div>
              </div>
            )}

            {/* The save-offer gate itself — reached by clicking "Cancel or pause
                subscription" above. Discloses the 3-month auto-resume + full rebill
                up front, at this first offer moment, not only after a further click
                into the deeper pause confirm box below. */}
            {manageOfferOpen && (
              <div className="settings-confirm-box">
                <p><strong>{t("settings.manageOfferTitle")}</strong></p>
                <p>
                  {t("settings.manageOfferSub", {
                    date: subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString(dateLocale) : "—",
                  })}
                </p>
                <button
                  className="settings-delete-final-btn"
                  style={{background:"#2E7D32"}}
                  onClick={() => { setManageOfferOpen(false); setPauseOfferOpen(true); setPauseError(""); }}
                >
                  {t("settings.manageOfferPauseBtn")}
                </button>
                <button
                  className="settings-cancel-btn"
                  onClick={() => { setManageOfferOpen(false); openManageLink("cancel_subscription"); }}
                  disabled={manageLinkBusy}
                >
                  {manageLinkBusy ? t("settings.manageLinkLoading") : t("settings.manageOfferContinueLink")}
                </button>
                <button className="settings-cancel-btn" onClick={() => setManageOfferOpen(false)}>{t("settings.manageOfferDismiss")}</button>
                {manageLinkError && <p style={{color:"#c0392b",fontSize:"0.8125rem",margin:"8px 0 0"}}>{manageLinkError}</p>}
              </div>
            )}

            {pauseOfferOpen && (
              <div className="settings-confirm-box">
                <p><strong>{t("settings.pauseConfirmP1")}</strong></p>
                <ul>
                  <li>{t("settings.pauseConfirmC1", { date: subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString(dateLocale) : "—" })}</li>
                  <li>{t("settings.pauseConfirmC2")}</li>
                  <li>{t("settings.pauseConfirmC3")}</li>
                </ul>
                {pauseError && <p style={{color:"#c0392b",fontSize:"0.8125rem",margin:"8px 0 0"}}>{pauseError}</p>}
                <button className="settings-delete-final-btn" onClick={confirmPause} disabled={pauseBusy}>
                  {pauseBusy ? t("settings.pausing") : t("settings.pauseConfirmBtn")}
                </button>
                <button className="settings-cancel-btn" onClick={() => setPauseOfferOpen(false)}>{t("settings.cancelBtn")}</button>
              </div>
            )}
          </>
        ) : subscription?.status === "paused" ? (
          <>
            <div className="settings-row">
              <div>
                <div className="settings-row-label">{t("settings.subscriptionPausedLabel")}</div>
                <div className="settings-row-sub">
                  {subscription.resumeAt
                    ? t("settings.subscriptionPausedSub", { date: new Date(subscription.resumeAt).toLocaleDateString(dateLocale) })
                    : t("settings.subscriptionPausedSubNoDate")}
                </div>
                {lastChargeLine}
              </div>
              <button className="settings-action-btn" onClick={() => { setResumeStep(true); setResumeError(""); }}>
                {t("settings.subscriptionResumeBtn")}
              </button>
            </div>
            {resumeStep && (
              <div className="settings-confirm-box">
                <p><strong>{t("settings.resumeConfirmP1")}</strong></p>
                <ul>
                  <li>{t("settings.resumeConfirmC1")}</li>
                </ul>
                {resumeError && <p style={{color:"#c0392b",fontSize:"0.8125rem",margin:"8px 0 0"}}>{resumeError}</p>}
                <button className="settings-delete-final-btn" onClick={confirmResume} disabled={resumeBusy}>
                  {resumeBusy ? t("settings.subscriptionResuming") : t("settings.resumeConfirmBtn")}
                </button>
                <button className="settings-cancel-btn" onClick={() => setResumeStep(false)}>{t("settings.cancelBtn")}</button>
              </div>
            )}
          </>
        ) : subscription?.status === "cancelled" && subscription.currentPeriodEnd ? (
          <div className="settings-row">
            <div>
              <div className="settings-row-label">
                {t(subscription.tier === VOICE_TIER_ELITE ? "settings.subscriptionElitePlan" : "settings.subscriptionProPlan", { plan: subscription.planType === "yearly" ? t("settings.subscriptionPlanAnnual") : t("settings.subscriptionPlanMonthly") })}
              </div>
              <div className="settings-row-sub">
                {t("settings.subscriptionCancelledPending", { date: new Date(subscription.currentPeriodEnd).toLocaleDateString(dateLocale) })}
              </div>
              {lastChargeLine}
            </div>
            <button className="settings-action-btn" onClick={() => openManageLink()} disabled={manageLinkBusy}>
              {manageLinkBusy ? t("settings.manageLinkLoading") : t("settings.subscriptionManageLink")}
            </button>
          </div>
        ) : (
          <div className="settings-row">
            <div>
              <div className="settings-row-label">{t("paywall.settingsFreePlan")}</div>
              <div className="settings-row-sub">
                {profile?.trialStartDate
                  ? trialDaysLeft(profile) > 0
                    ? t("paywall.settingsTrialRemaining", { n: trialDaysLeft(profile), s: trialDaysLeft(profile) !== 1 ? "s" : "" })
                    : t("paywall.settingsTrialEnded")
                  : t("paywall.settingsFreeForever")}
              </div>
            </div>
            <button className="settings-action-btn" onClick={() => onUpgrade?.()} style={{background:"#7B1E1E",color:"#fff",borderColor:"#7B1E1E"}}>
              {t("paywall.settingsUpgradeBtn")}
            </button>
          </div>
        )}

        {/* A6 (item 8): persistent voice-minutes display, checkable any time — distinct from
            AgentChat's own reactive low-balance banners, which stay exactly as they were. Shown
            for anyone with voice entitlement at all, independent of which branch above matched. */}
        {voiceEntitled && (
          <div className="settings-row" style={{borderTop:"1px solid #F0DFC0",marginTop:12,paddingTop:12}}>
            <div>
              <div className="settings-row-label">
                {t("settings.voiceMinutesLabel", { tier:
                    isAdminAccount ? t("settings.voiceTierAdmin")
                  : isSimpleViewer ? t("settings.voiceTierViewer")
                  : onVoiceTrial ? t("settings.voiceTierTrial")
                  : voiceTier === VOICE_TIER_ELITE ? t("settings.voiceTierElite")
                  : t("settings.voiceTierBasic") })}
              </div>
              <div className="settings-row-sub">
                {t("settings.voiceMinutesUsed", { used: formatVoiceDuration(voiceUsedSeconds), limit: formatVoiceMinutes(voiceLimitSeconds) })}
              </div>
              {voiceBalanceResolved.purchasedSeconds > 0 && (
                <div className="settings-row-sub">
                  {t("settings.voiceMinutesPurchasedExtra", { extra: formatVoiceMinutes(voiceBalanceResolved.purchasedSeconds) })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Your Data ── */}
      <div className="goals-card">
        <div className="goals-section-title">{t("settings.yourDataSection")}</div>

        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t("settings.marketingLabel")}</div>
            <div className="settings-row-sub">{t("settings.marketingSub")}</div>
          </div>
          <input
            type="checkbox"
            checked={profile?.consentMarketing ?? false}
            onChange={e => onUpdateProfile?.({ consentMarketing: e.target.checked })}
            style={{width:20,height:20,accentColor:"#7B1E1E",cursor:"pointer",flexShrink:0}}
          />
        </div>

        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t("settings.voiceConsentLabel")}</div>
            <div className="settings-row-sub">{t("settings.voiceConsentSub")}</div>
          </div>
          <input
            type="checkbox"
            checked={hasValidVoiceConsent(profile)}
            onChange={e => onUpdateProfile?.({
              voiceConsent: e.target.checked
                ? { accepted: true, version: VOICE_CONSENT_VERSION, acceptedAt: new Date().toISOString() }
                : { accepted: false },
            })}
            style={{width:20,height:20,accentColor:"#7B1E1E",cursor:"pointer",flexShrink:0}}
          />
        </div>

        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t("settings.exportLabel")}</div>
            <div className="settings-row-sub">{t("settings.exportSub")}</div>
          </div>
          <button className="settings-action-btn" onClick={handleExport}>
            {exportDone ? t("settings.exportDone") : t("settings.exportBtn")}
          </button>
        </div>

        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t("settings.cacheLabel")}</div>
            <div className="settings-row-sub">{t("settings.cacheSub")}</div>
          </div>
        </div>
      </div>

      {/* ── Privacy, Terms & Legal ── */}
      <div className="goals-card">
        <div className="goals-section-title">{t("settings.privacyLegalSection")}</div>

        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t("settings.privacyPolicyLabel")}</div>
            <div className="settings-row-sub">{t("settings.privacyPolicySub")}</div>
          </div>
          <a href="https://speafy.com/privacy" target="_blank" rel="noopener noreferrer" className="settings-action-btn" style={{textDecoration:"none"}}>{t("settings.viewLink")}</a>
        </div>

        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t("settings.termsLabel")}</div>
            <div className="settings-row-sub">{t("settings.termsSub")}</div>
          </div>
          <a href="https://speafy.com/terms" target="_blank" rel="noopener noreferrer" className="settings-action-btn" style={{textDecoration:"none"}}>{t("settings.viewLink")}</a>
        </div>

        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t("settings.refundLabel")}</div>
            <div className="settings-row-sub">{t("settings.refundSub")}</div>
          </div>
          <a href="https://speafy.com/refund" target="_blank" rel="noopener noreferrer" className="settings-action-btn" style={{textDecoration:"none"}}>{t("settings.viewLink")}</a>
        </div>

        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t("settings.cookieSettingsLabel")}</div>
            <div className="settings-row-sub">{t("settings.cookieSettingsSub")}</div>
          </div>
          <a href="https://speafy.com?cookies=1" target="_blank" rel="noopener noreferrer" className="settings-action-btn" style={{textDecoration:"none"}}>{t("settings.manageLink")}</a>
        </div>
      </div>

      {/* ── About ── */}
      <div className="goals-card">
        <div className="goals-section-title">{t("settings.aboutSection")}</div>
        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t("settings.aiTutorLabel")}</div>
            <div className="settings-row-sub">{t("settings.aiTutorSub")}</div>
          </div>
        </div>
        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t("settings.algoLabel")}</div>
            <div className="settings-row-sub">{t("settings.algoSub")}</div>
          </div>
        </div>
        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t("settings.versionLabel")}</div>
            <div className="settings-row-sub">Speafy v{process.env.REACT_APP_VERSION} · {formatNumber(new Date().getFullYear(), lang)}</div>
          </div>
        </div>
      </div>

      {/* ── Appearance ── */}
      <div className="goals-card">
        <div className="goals-section-title">{t("settings.appearanceSection")}</div>
        <div className="settings-row" style={{borderBottom:"none",paddingBottom:0}}>
          <div>
            <div className="settings-row-label">{t("settings.darkModeLabel")}</div>
            <div className="settings-row-sub">{t("settings.darkModeSub")}</div>
          </div>
          <label className="theme-toggle" aria-label="Toggle dark mode">
            <input type="checkbox" checked={darkMode} onChange={toggleDark} />
            <div className="theme-toggle-track" />
            <div className="theme-toggle-thumb" />
          </label>
        </div>
        <div className="settings-row" style={{borderBottom:"none",paddingBottom:0}}>
          <div>
            <div className="settings-row-label">{t("settings.largeTextLabel")}</div>
            <div className="settings-row-sub">{t("settings.largeTextSub")}</div>
          </div>
          <div className="font-scale-selector">
            {[["normal","A"],["large","A+"],["xl","A++"]].map(([val, lbl]) => (
              <button key={val} className={`font-scale-btn${fontScale === val ? " active" : ""}`}
                onClick={() => setFontScale(val)} aria-pressed={fontScale === val}>
                <bdi>{lbl}</bdi>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Babi Check-ins ── */}
      <div className="goals-card">
        <div className="goals-section-title">{t("settings.babiCheckinsSection")}</div>
        <div className="settings-row" style={{borderBottom:"none",paddingBottom:0}}>
          <div>
            <div className="settings-row-label">{t("settings.babiDailyNudgeLabel")}</div>
            <div className="settings-row-sub">{t("settings.babiDailyNudgeSub")}</div>
          </div>
          <label className="theme-toggle" aria-label={t("settings.babiDailyNudgeLabel")}>
            <input
              type="checkbox"
              checked={profile?.babiDailyNudgeEnabled ?? true}
              onChange={e => onUpdateProfile?.({ babiDailyNudgeEnabled: e.target.checked })}
            />
            <div className="theme-toggle-track" />
            <div className="theme-toggle-thumb" />
          </label>
        </div>
        <div className="settings-row" style={{borderBottom:"none",paddingBottom:0}}>
          <div>
            <div className="settings-row-label">{t("settings.babiWeeklyReportLabel")}</div>
            <div className="settings-row-sub">{t("settings.babiWeeklyReportSub")}</div>
          </div>
          <label className="theme-toggle" aria-label={t("settings.babiWeeklyReportLabel")}>
            <input
              type="checkbox"
              checked={profile?.babiWeeklyReportEnabled ?? true}
              onChange={e => onUpdateProfile?.({ babiWeeklyReportEnabled: e.target.checked })}
            />
            <div className="theme-toggle-track" />
            <div className="theme-toggle-thumb" />
          </label>
        </div>
      </div>

      {/* ── Danger Zone ── */}
      <div className="settings-danger-card">
        <div className="settings-danger-title">{t("settings.dangerSection")}</div>

        {/* Reset progress */}
        <div className="settings-row" style={{borderColor:"#FBBFBF"}}>
          <div>
            <div className="settings-row-label" style={{color:"#C0392B"}}>{t("settings.resetLabel")}</div>
            <div className="settings-row-sub">{t("settings.resetSub")}</div>
          </div>
          <button className="settings-danger-btn" onClick={() => { setResetStep(true); setDeleteStep(false); }}>
            {t("settings.resetBtn")}
          </button>
        </div>
        {resetStep && (
          <div className="settings-confirm-box">
            <p><strong>{t("settings.resetConfirmP1")}</strong></p>
            <ul>
              <li>{t("settings.resetConfirmC1", { n: cards.length })}</li>
              <li>{t("settings.resetConfirmC2", { n: progress?.streak || 0 })}</li>
              <li>{t("settings.resetConfirmC3")}</li>
              <li>{t("settings.resetConfirmC4")}</li>
            </ul>
            <p>{t("settings.resetConfirmP2")}</p>
            <button className="settings-delete-final-btn" style={{background:"#C0392B"}} onClick={confirmReset} disabled={busy}>
              {busy ? t("settings.resetting") : t("settings.resetConfirmBtn")}
            </button>
            <button className="settings-cancel-btn" onClick={() => setResetStep(false)}>{t("settings.cancelBtn")}</button>
          </div>
        )}
        {resetDone && <div className="success-msg" style={{marginTop:12}}>{t("settings.resetDone")}</div>}

        {/* Delete account */}
        <div className="settings-row" style={{borderBottom:"none", paddingBottom:0, borderColor:"#FBBFBF"}}>
          <div>
            <div className="settings-row-label" style={{color:"#C0392B"}}>{t("settings.deleteLabel")}</div>
            <div className="settings-row-sub">{t("settings.deleteSub")}</div>
          </div>
          <button className="settings-danger-btn" onClick={() => { setDeleteStep(true); setDeleteText(""); setResetStep(false); }}>
            {t("settings.deleteBtn")}
          </button>
        </div>
        {deleteStep && (
          <div className="settings-confirm-box">
            <p><strong>{t("settings.deleteConfirmP1")}</strong></p>
            <ul>
              <li>{t("settings.deleteConfirmC1")}</li>
              <li>{t("settings.deleteConfirmC2", { n: cards.length })}</li>
              <li>{t("settings.deleteConfirmC3")}</li>
              <li>{t("settings.deleteConfirmC4")}</li>
            </ul>
            <p>{t("settings.deleteConfirmPrompt")}</p>
            <input
              className="settings-confirm-input"
              placeholder="DELETE"
              value={deleteText}
              onChange={e => { setDeleteText(e.target.value); setDeleteError(""); }}
              autoComplete="off"
            />
            {isEmailUser && (
              <input
                className="settings-confirm-input"
                type="password"
                placeholder="Your password"
                value={deletePassword}
                onChange={e => { setDeletePassword(e.target.value); setDeleteError(""); }}
                style={{marginTop:8}}
                autoComplete="current-password"
              />
            )}
            {deleteError && <p style={{color:"#c0392b",fontSize:"0.8125rem",margin:"8px 0 0"}}>{deleteError}</p>}
            <button
              className="settings-delete-final-btn"
              onClick={confirmDelete}
              disabled={deleteText !== "DELETE" || busy}
            >
              {busy ? t("settings.deleting") : t("settings.deleteConfirmBtn")}
            </button>
            <button className="settings-cancel-btn" onClick={() => { setDeleteStep(false); setDeleteText(""); setDeletePassword(""); setDeleteError(""); }}>{t("settings.cancelBtn")}</button>
          </div>
        )}
      </div>
    </div>
  );
}
